import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { MessageCircle, User, Star, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { getListing, storeBadge, storeCtaLabel } from '../lib/listings';
import { getSellerProfile, memberSinceLabel, responseRateLabel, type SellerProfile } from '../lib/sellers';
import { sendMessage } from '../lib/messages';
import { getReviews, createReview } from '../lib/reviews';
import { getListingImages } from '../lib/storage';
import { getBrandImageUrl } from '../lib/brandImages';
import { brands } from '../data/brands';
import LikeButton from '../components/LikeButton';
import ReviewsList from '../components/ReviewsList';
import ReviewModal from '../components/ReviewModal';
import RewardBanner from '../components/RewardBanner';
import SEO, { SITE_URL } from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import ShareButtons from '../components/ShareButtons';
import { track } from '../lib/analytics';

const CONDITION_SCHEMA: Record<string, string> = {
  'new': 'https://schema.org/NewCondition',
  'like-new': 'https://schema.org/UsedCondition',
  'gently-used': 'https://schema.org/UsedCondition',
  'well-used': 'https://schema.org/UsedCondition',
};

function ListingDetails() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [listing, setListing] = useState<any>(null);
  const [images, setImages] = useState<string[]>([]);
  const [selectedImage, setSelectedImage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showMessageInput, setShowMessageInput] = useState(false);
  const [messageContent, setMessageContent] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [reviews, setReviews] = useState<any[]>([]);
  const [loadingReviews, setLoadingReviews] = useState(true);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);
  const [sellerProfile, setSellerProfile] = useState<SellerProfile | null>(null);

  useEffect(() => {
    if (id) {
      loadListing(id);
    }
  }, [id]);

  useEffect(() => {
    // Store (ClearanceWigs) listings have no peer seller to review.
    if (listing?.source === 'clearancewigs') {
      setLoadingReviews(false);
      return;
    }
    if (listing?.user_id) {
      loadReviews(listing.user_id);
      getSellerProfile(listing.user_id).then(setSellerProfile).catch(() => {});
    }
  }, [listing?.user_id, listing?.source]);

  const loadReviews = async (userId: string) => {
    try {
      const { reviews: userReviews, error } = await getReviews(userId);
      if (error) throw error;
      setReviews(userReviews);
    } catch (error) {
      console.error('Error loading reviews:', error);
      toast.error('Failed to load reviews');
    } finally {
      setLoadingReviews(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please sign in to send messages');
      return;
    }
    if (!messageContent.trim()) return;

    console.log('Sending message to:', listing.user_id, 'for listing:', listing.id);
    setSendingMessage(true);
    try {
      const { message, error } = await sendMessage(listing.user_id, messageContent.trim(), null, listing.id);
      if (error) { 
        if (error.message === 'Not authenticated') {
          toast.error('Please sign in to send messages');
          return;
        }
        throw error;
      }
      
      console.log('Message sent successfully:', message);
      
      track('contact_seller', { listing_id: listing.id, brand: listing.brand });
      toast.success('Message sent successfully!');
      setMessageContent('');
      setShowMessageInput(false);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  };

  const loadListing = async (listingId: string) => {
    try {
      const [{ listing, error }, { urls, error: imageError }] = await Promise.all([
        getListing(listingId),
        getListingImages(listingId)
      ]);

      if (error) throw error;

      // getListingImages() is supplementary — it lists extra images from the
      // owner's storage folder and REQUIRES auth (returns an error for anonymous
      // visitors). It must never fail the page: the listing's own images come from
      // listing.image_urls (and store/ClearanceWigs listings have no storage folder).
      const storageUrls = imageError ? [] : urls;

      setListing(listing);
      if (listing) {
        track('listing_view', { listing_id: listing.id, brand: listing.brand, source: listing.source || 'user' });
      }
      setImages(storageUrls);

      // Canonicalize legacy UUID URLs to the SEO slug (client-side 301 equivalent).
      if (listing?.slug && listingId !== listing.slug) {
        navigate(`/listing/${listing.slug}`, { replace: true });
      }
    } catch (error) {
      console.error('Error loading listing:', error);
      toast.error('Failed to load listing');
    } finally {
      setLoading(false);
    }
  };

  const handleReviewSubmit = async (rating: number, comment: string) => {
    if (!listing?.user_id) return;
    
    try {
      const { review, error } = await createReview(listing.user_id, rating, comment);
      if (error) throw error;
      
      // Add the new review to the list
      setReviews(prev => [review, ...prev]);
      toast.success('Review submitted successfully');
      setShowReviewModal(false);
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Failed to submit review');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600"></div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Listing not found</h2>
          <p className="mt-2 text-gray-600">This listing may have been removed or is no longer available.</p>
          <Link to="/" className="mt-4 inline-block text-rose-600 hover:text-rose-700">
            Return to home
          </Link>
        </div>
      </div>
    );
  }

  const isStore = listing.source === 'clearancewigs';
  const brandName = brands[listing.brand]?.name || listing.brand;
  const canonicalPath = `/listing/${listing.slug || listing.id}`;
  const ogImage = listing.image_urls?.[0] || images[0];
  const metaTitle = `${listing.title} — ${brandName} ${listing.condition} wig for $${listing.price}`;
  const metaDescription = (listing.description || `Pre-owned ${brandName} wig for sale on WigConnection.`)
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 300);
  const productJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: listing.title,
    ...(Array.isArray(listing.image_urls) && listing.image_urls.length > 0 ? { image: listing.image_urls } : {}),
    description: metaDescription,
    brand: { '@type': 'Brand', name: brandName },
    itemCondition: CONDITION_SCHEMA[listing.condition] || 'https://schema.org/UsedCondition',
    offers: {
      '@type': 'Offer',
      price: listing.price,
      priceCurrency: 'USD',
      availability: listing.status === 'active' ? 'https://schema.org/InStock' : 'https://schema.org/SoldOut',
      url: `${SITE_URL}${canonicalPath}`,
    },
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEO
        title={metaTitle}
        description={metaDescription}
        canonical={canonicalPath}
        image={ogImage}
        type="product"
        jsonLd={productJsonLd}
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Image Gallery */}
        <div className="space-y-4">
          <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 relative group">
            <img
              src={listing.image_urls?.[selectedImage] || images[selectedImage] || getBrandImageUrl(listing.brand)}
              alt={listing.title}
              className="w-full h-full object-cover"
            />
            
            {/* Image Counter */}
            {(listing.image_urls?.length > 1 || images.length > 1) && (
              <div className="absolute bottom-4 right-4 bg-black bg-opacity-60 text-white text-sm px-3 py-1 rounded-full">
                {selectedImage + 1} / {(listing.image_urls?.length || images.length)}
              </div>
            )}
            
            {/* Left Arrow */}
            {(listing.image_urls?.length > 1 || images.length > 1) && selectedImage > 0 && (
              <button 
                onClick={() => setSelectedImage(prev => prev - 1)}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white bg-opacity-70 hover:bg-opacity-100 rounded-full p-2 shadow-md opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                aria-label="Previous image"
              >
                <ChevronLeft className="h-6 w-6 text-gray-800" />
              </button>
            )}
            
            {/* Right Arrow */}
            {(listing.image_urls?.length > 1 || images.length > 1) && selectedImage < ((listing.image_urls?.length || images.length) - 1) && (
              <button 
                onClick={() => setSelectedImage(prev => prev + 1)}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white bg-opacity-70 hover:bg-opacity-100 rounded-full p-2 shadow-md opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                aria-label="Next image"
              >
                <ChevronRight className="h-6 w-6 text-gray-800" />
              </button>
            )}
          </div>
          
          {/* Thumbnails */}
          {(listing.image_urls?.length > 1 || images.length > 1) && (
            <div className="grid grid-cols-5 gap-2 mt-3">
              {(listing.image_urls || images).map((image: string, index: number) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`aspect-square rounded-lg overflow-hidden border-2 ${selectedImage === index ? 'border-rose-500' : 'border-transparent'}`}
                >
                  <img
                    src={image}
                    alt={`${listing.title} - Image ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Listing Details */}
        <div className="space-y-6">
          {!isStore && <RewardBanner variant="detail" />}
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{listing.title}</h1>
            <div className="flex items-center mt-2 mb-4">
              <div className="flex flex-col">
                {(() => {
                  const sellerInner = (
                    <>
                      {listing.owner?.avatar_url ? (
                        <img
                          src={listing.owner.avatar_url}
                          alt={listing.owner?.display_name || listing.owner?.full_name || 'Seller'}
                          className="w-8 h-8 rounded-full mr-2"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center mr-2">
                          <User className="w-4 h-4 text-gray-500" />
                        </div>
                      )}
                      <span className="font-medium">
                        {listing.owner?.display_name || listing.owner?.full_name || 'Member'}
                      </span>
                      {listing.owner?.rating > 0 && (
                        <div className="flex items-center ml-2">
                          <Star className="h-4 w-4 text-yellow-400 fill-current" />
                          <span className="ml-1">{listing.owner.rating.toFixed(1)}</span>
                          <span className="text-gray-500 ml-1">({listing.owner.review_count})</span>
                        </div>
                      )}
                    </>
                  );
                  return listing.user_id && !isStore ? (
                    <Link
                      to={`/user/${listing.user_id}`}
                      className="flex items-center text-gray-600 hover:text-gray-900"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {sellerInner}
                    </Link>
                  ) : (
                    <div className="flex items-center text-gray-600">{sellerInner}</div>
                  );
                })()}
                {isStore && (
                  <span className="inline-flex items-center gap-1.5 w-fit mt-1">
                    <span className="inline-flex items-center bg-rose-100 text-rose-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                      Sold by ClearanceWigs (official)
                    </span>
                    <span className="inline-flex items-center bg-rose-600 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                      {storeBadge(listing)}
                    </span>
                  </span>
                )}
                {listing.user_id && !isStore && sellerProfile && (
                  <div className="ml-10 mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
                    {sellerProfile.member_since && (
                      <span>Member since {memberSinceLabel(sellerProfile.member_since)}</span>
                    )}
                    {sellerProfile.sold_count > 0 && (
                      <>
                        <span aria-hidden>•</span>
                        <span>{sellerProfile.sold_count} sold</span>
                      </>
                    )}
                    {responseRateLabel(sellerProfile.response_rate) && (
                      <span className="inline-flex items-center rounded-full bg-green-50 text-green-700 px-2 py-0.5 font-medium">
                        {responseRateLabel(sellerProfile.response_rate)}
                      </span>
                    )}
                  </div>
                )}
                {listing.user_id && !isStore && (
                  <Link
                    to={`/user/${listing.user_id}`}
                    className="text-sm text-rose-600 hover:text-rose-700 ml-10 mt-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    View seller profile
                  </Link>
                )}
              </div>
            </div>
            <p className="mt-2 text-xl font-bold text-rose-600">${listing.price}</p>
            {listing.original_price && (
              <p className="mt-1 text-sm text-gray-500">
                Original Price Paid: ${listing.original_price}
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <LikeButton listingId={listing.id} showCount />
            <ShareButtons
              url={`${window.location.origin}/listing/${listing.slug || listing.id}`}
              title={listing.title}
              imageUrl={listing.image_urls?.[0]}
              context="listing_detail"
            />
          </div>

          <div className="border-t border-b py-4 space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Brand</span>
              <span className="font-medium">{listing.brand}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Condition</span>
              <span className="font-medium">
                {isStore
                  ? (storeBadge(listing) === 'Open-box' ? 'Open-box' : 'New (outlet price)')
                  : listing.condition}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Color</span>
              <span className="font-medium">{listing.color}</span>
            </div>
            {listing.fiber_type && (
              <div className="flex justify-between">
                <span className="text-gray-600">Type of Fiber</span>
                <span className="font-medium">{listing.fiber_type}</span>
              </div>
            )}
            {listing.cap_size && (
              <div className="flex justify-between">
                <span className="text-gray-600">Cap Size</span>
                <span className="font-medium">{listing.cap_size}</span>
              </div>
            )}
            {listing.modifications && (
              <div className="flex justify-between">
                <span className="text-gray-600">Modifications</span>
                <span className="font-medium">{listing.modifications}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-600">Type</span>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                listing.type === 'sale' ? 'bg-green-100 text-green-800' :
                listing.type === 'trade' ? 'bg-blue-100 text-blue-800' :
                'bg-purple-100 text-purple-800'
              }`}>
                {listing.type === 'sale' ? 'For Sale' :
                 listing.type === 'trade' ? 'For Trade' :
                 'Sale or Trade'}
              </span>
            </div>
          </div>

          <div>
            <h2 className="text-lg font-medium mb-2">Description</h2>
            {(() => {
              const desc: string = listing.description || '';
              const isLong = desc.length > 600;
              const shown = isLong && !descExpanded ? desc.slice(0, 600).trimEnd() + '…' : desc;
              return (
                <>
                  <p className="text-gray-600 whitespace-pre-line">{shown}</p>
                  {isLong && (
                    <button
                      type="button"
                      onClick={() => setDescExpanded((v) => !v)}
                      className="mt-2 text-sm font-medium text-rose-600 hover:text-rose-700"
                    >
                      {descExpanded ? 'Read less' : 'Read more'}
                    </button>
                  )}
                </>
              );
            })()}
          </div>

          <div className="pt-6">
            {isStore ? (
              <div className="space-y-2">
                <a
                  href={listing.external_url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="w-full flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-rose-600 hover:bg-rose-700"
                >
                  <ExternalLink className="h-5 w-5 mr-2" />
                  {storeCtaLabel(listing)}
                </a>
                <p className="text-xs text-center text-gray-500">
                  You'll be taken to ClearanceWigs.com to complete your purchase.
                </p>
              </div>
            ) : !user ? (
              <button
                onClick={() => {
                  toast('Sign in to contact the seller');
                  navigate('/login', { state: { from: window.location.pathname } });
                }}
                className="w-full flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-rose-600 hover:bg-rose-700"
              >
                <MessageCircle className="h-5 w-5 mr-2" />
                Sign in to Contact Seller
              </button>
            ) : !showMessageInput ? (
              <button
                onClick={() => setShowMessageInput(true)}
                className="w-full flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-rose-600 hover:bg-rose-700"
              >
                <MessageCircle className="h-5 w-5 mr-2" />
                Contact Seller
              </button>
            ) : (
              <form onSubmit={handleSendMessage} className="space-y-3">
                <textarea
                  value={messageContent}
                  onChange={(e) => setMessageContent(e.target.value)}
                  placeholder="Type your message..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-rose-500 focus:border-rose-500"
                  rows={3}
                  disabled={sendingMessage}
                />
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowMessageInput(false)}
                    className="px-4 py-2 text-gray-700 hover:text-gray-900"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={sendingMessage || !messageContent.trim()}
                    className="px-6 py-2 bg-rose-600 text-white rounded-md hover:bg-rose-700 disabled:opacity-50"
                  >
                    {sendingMessage ? 'Sending...' : 'Send Message'}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Show link to messages page if user has sent a message */}
          {!isStore && (
            <div className="mt-4 text-center">
              <Link
                to="/messages"
                className="text-sm text-rose-600 hover:text-rose-700"
              >
                View all messages
              </Link>
            </div>
          )}
        </div>

        {/* Reviews Section — peer sellers only (store listings have no reviews). */}
        {!isStore && (
          <div className="mt-12 pt-8 border-t">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Reviews</h2>
              {user && listing.user_id && user.id !== listing.user_id && (
                <button
                  onClick={() => setShowReviewModal(true)}
                  className="px-4 py-2 text-rose-600 hover:text-rose-700 font-medium"
                >
                  Write a Review
                </button>
              )}
            </div>
            <ReviewsList reviews={reviews} loading={loadingReviews} />
          </div>
        )}
      </div>

      {/* Review Modal */}
      {!isStore && (
        <ReviewModal
          isOpen={showReviewModal}
          onClose={() => setShowReviewModal(false)}
          onSubmit={handleReviewSubmit}
          userToReview={{
            id: listing.user_id,
            name: listing.owner?.display_name || listing.owner?.full_name || 'Seller'
          }}
        />
      )}
    </div>
  );
}

export default ListingDetails;